/**
 * Price Data Processor
 * バリアントメタフィールドから価格データを処理
 */

(function() {
  'use strict';

  /**
   * variation codeをprice_matrix内でマッチング（正規化対応 + 順序入れ替え対応）
   * @param {string} variationCode - 構築したvariation code
   * @param {Object} matrixData - price_matrixデータ { variations: { ... } }
   * @returns {string|null} - マッチしたvariation codeキー、見つからない場合はnull
   */
  function matchVariationCodeInMatrix(variationCode, matrixData) {
    if (!variationCode || !matrixData || !matrixData.variations) {
      return null;
    }

    const normalize = window.normalizeVariationCode || window.normalizeString || ((str) => str);
    const normalizedTarget = normalize(variationCode);
    const availableKeys = Object.keys(matrixData.variations);
    
    // 0. キーが1つしかない場合はそれを使用（バリアントごとに独立したprice_matrix）
    if (availableKeys.length === 1) {
      return availableKeys[0];
    }
    
    // 1. 直接マッチ
    if (matrixData.variations[variationCode]) {
      return variationCode;
    }
    
    // 2. 正規化マッチング
    let matchingKey = availableKeys.find(key => 
      normalize(key) === normalizedTarget
    );
    
    if (matchingKey) {
      return matchingKey;
    }
    
    // 3. 順序を入れ替えてマッチング（A_B → B_A）
    const parts = variationCode.split(window.VARIATION_CODE_SEPARATOR);
    if (parts.length === 2) {
      const reversedCode = parts[1] + window.VARIATION_CODE_SEPARATOR + parts[0];
      const normalizedReversed = normalize(reversedCode);
      
      // 直接マッチ（入れ替え後）
      if (matrixData.variations[reversedCode]) {
        return reversedCode;
      }
      
      // 正規化マッチング（入れ替え後）
      matchingKey = availableKeys.find(key => 
        normalize(key) === normalizedReversed
      );
      
      if (matchingKey) {
        return matchingKey;
      }
    }
    
    // 4. ページ範囲を除外してマッチング（3つ以上のパーツがある場合）
    // 例: 16-28P_マット90kg_全頁カラー → マット90kg_全頁カラー
    if (parts.length >= 3) {
      // ページ範囲形式（数字-数字P）を検出
      const pageRangeIndex = parts.findIndex(p => /^\d+-\d+P$/.test(p));
      if (pageRangeIndex >= 0) {
        const partsWithoutPageRange = parts.filter((_, i) => i !== pageRangeIndex);
        const codeWithoutPageRange = partsWithoutPageRange.join(window.VARIATION_CODE_SEPARATOR);
        const normalizedWithoutPageRange = normalize(codeWithoutPageRange);
        
        // 直接マッチ
        if (matrixData.variations[codeWithoutPageRange]) {
          return codeWithoutPageRange;
        }
        
        // 正規化マッチング
        matchingKey = availableKeys.find(key => 
          normalize(key) === normalizedWithoutPageRange
        );
        
        if (matchingKey) {
          return matchingKey;
        }
        
        // 順序入れ替え
        if (partsWithoutPageRange.length === 2) {
          const reversed = partsWithoutPageRange[1] + window.VARIATION_CODE_SEPARATOR + partsWithoutPageRange[0];
          if (matrixData.variations[reversed]) {
            return reversed;
          }
          matchingKey = availableKeys.find(key => 
            normalize(key) === normalize(reversed)
          );
          if (matchingKey) {
            return matchingKey;
          }
        }
      }
    }
    
    // 5. 部分マッチング（最終手段）
    matchingKey = availableKeys.find(key => {
      const normalizedKey = normalize(key);
      const keyParts = normalizedKey.split(window.VARIATION_CODE_SEPARATOR);
      const targetParts = normalizedTarget.split(window.VARIATION_CODE_SEPARATOR);
      
      // 両方の部分が含まれているかチェック
      return targetParts.every(part => keyParts.includes(part));
    });
    
    if (matchingKey) {
      return matchingKey;
    }
    
    // 6. FSC無線綴じ冊子などの特殊なケース：「標準」や空のvariationCodeの場合
    //    最初のキーをデフォルトとして使用
    if (variationCode === window.VARIATION_CODE_STANDARD || variationCode === '' || !variationCode.includes(window.VARIATION_CODE_SEPARATOR)) {
      return availableKeys[0];
    }
    
    return null;
  }

  /**
   * バリアントメタフィールドから価格データを処理
   * @param {Array} variantMetafields - バリアントメタフィールド配列
   * @param {Array} shopifyOptions - Shopifyオプション定義
   * @param {Object} options - オプション設定
   * @param {string} options.filterPageRange - 処理するページ範囲（例: '24-32P'）
   * @param {boolean} options.prioritizePageRange - ページ範囲フィルタリングを優先
   * @returns {Object} - 処理された価格データ
   */
  function processPriceData(variantMetafields, shopifyOptions, options = {}) {
    const startTime = performance.now();

    if (!Array.isArray(variantMetafields) || !Array.isArray(shopifyOptions)) {
      console.warn('[Price Data Processor] Invalid input:', {
        variantMetafieldsIsArray: Array.isArray(variantMetafields),
        shopifyOptionsIsArray: Array.isArray(shopifyOptions)
      });
      return {
        variantPriceData: [],
        variationsData: {},
        hasVariantMetafields: false
      };
    }

    // ページ範囲フィルタリングの設定
    const filterPageRange = options.filterPageRange || null;
    const prioritizePageRange = options.prioritizePageRange || false;

    const variantPriceData = [];
    const variationsData = {};
    let hasVariantMetafields = false;
    let processedCount = 0;
    let skippedNoMatrixCount = 0;
    let skippedNoMatchCount = 0;
    let skippedByFilterCount = 0;

    variantMetafields.forEach((variantMeta, index) => {
      if (!variantMeta.matrix) {
        skippedNoMatrixCount++;
        return;
      }

      // ページ範囲フィルタリング（優先処理モード）
      if (filterPageRange && prioritizePageRange) {
        const variantPageRange = variantMeta.option1 || variantMeta.option2 || '';
        if (variantPageRange !== filterPageRange) {
          skippedByFilterCount++;
          return;
        }
      }

      processedCount++;

      // バリアントオプション値を取得（動的）
      const variantOptions = window.buildVariantOptions ? window.buildVariantOptions(variantMeta, shopifyOptions) : {};

      // variation codeを構築（正規化を有効にする - 全角/半角の違いを吸収）
      const variationCode = window.buildVariationCode ? window.buildVariationCode(variantOptions, shopifyOptions, true) : window.VARIATION_CODE_STANDARD;

      // 正規化マッチングでprice_matrixからデータを取得
      const matrixData = variantMeta.matrix;
      const availableVariationKeys = Object.keys(matrixData.variations || {});

      // 冊子商品判定: 本文紙質オプションがあり、variation_keysに表紙紙質キーがある場合
      const bodyPaper = variantOptions['本文紙質'];
      // window.isPaperKey を使用（variation-code-utils.js で定義）
      const hasCoverPaperKey = availableVariationKeys.some(key => window.isPaperKey(key));

      // 正規化形式の冊子商品検出: variation_keysが「表紙紙質_本文紙質」パターンの場合
      // 例: FSC認証用紙コート110kg_FSC認証用紙コート90kg
      const isNormalizedBookletFormat = (() => {
        // 「タイプ: 標準」のみの単一バリアント商品でvariation_keysがある場合
        if (variantOptions['タイプ'] === window.VARIATION_CODE_STANDARD && availableVariationKeys.length > 1) {
          // variation_keysが「X_Y」形式で、両方が紙質名の場合
          const isPaperPattern = (key) => {
            if (!key || key === window.VARIATION_CODE_STANDARD) return false;
            const parts = key.split(window.VARIATION_CODE_SEPARATOR);
            if (parts.length !== 2) return false;
            // 両方のパーツが紙質名かチェック（window.isPaperKey を使用）
            return window.isPaperKey(parts[0]) && window.isPaperKey(parts[1]);
          };
          return availableVariationKeys.some(isPaperPattern);
        }
        return false;
      })();

      const isBookletProduct = (bodyPaper && hasCoverPaperKey) || isNormalizedBookletFormat;

      // 単一バリアント＋複数用紙variation形式の検出（中綴じ冊子モノクロ等）
      // バリアントオプションが「標準」のみで、variations内に複数の用紙キーがある場合
      const isSingleVariantMultiplePaperProduct = (() => {
        // バリアントが「標準」または用紙オプションがない
        const hasStandardOnly = variationCode === window.VARIATION_CODE_STANDARD || !variationCode || !variationCode.includes(window.VARIATION_CODE_SEPARATOR);
        if (!hasStandardOnly) return false;

        // variation keysが2つ以上ある
        if (availableVariationKeys.length <= 1) return false;

        // 冊子商品でない
        if (isBookletProduct) return false;

        // variation keysが用紙名のパターン（window.isPaperKeyExtended を使用）
        const paperKeyCount = availableVariationKeys.filter(key => window.isPaperKeyExtended(key)).length;

        // 半数以上が用紙名パターンなら用紙選択商品と判断
        return paperKeyCount >= availableVariationKeys.length * 0.5;
      })();

      if (isSingleVariantMultiplePaperProduct) {
        // 単一バリアント＋複数用紙: すべてのvariationsを処理
        hasVariantMetafields = true;

        for (const [paperKey, variationArray] of Object.entries(matrixData.variations)) {
          if (!variationArray || !Array.isArray(variationArray)) continue;

          // 数量を抽出
          const quantities = [...new Set(variationArray.map(item => item.quantity))].sort((a, b) => a - b);

          // variationsDataに追加（用紙名をキーとして使用）
          variationsData[paperKey] = {
            paper: paperKey,
            printColor: variantOptions['印刷色数'] || null,
            pageCount: variantOptions['ページ数'] || variantOptions['ページ範囲'] || null,
            quantities: quantities
          };

          // 配列をpriceDataに追加
          // APIレスポンスは delivery_days/price、Liquidは delivery_time/base_price を使用するため両方に対応
          variationArray.forEach(item => {
            const price = item.base_price || item.price;
            let deliveryTime = item.delivery_time;
            if (!deliveryTime && item.delivery_days !== undefined) {
              deliveryTime = `${item.delivery_days + 1}日`;
            }

            if (item.quantity && deliveryTime && price && price > 0) {
              const priceEntry = {
                variation_code: paperKey,  // 用紙名をvariation_codeとして使用
                quantity: item.quantity,
                delivery_time: deliveryTime,
                base_price: price
              };

              // page_countがある場合は追加
              if (item.page_count !== undefined && item.page_count !== null) {
                priceEntry.page_count = item.page_count;
              }

              // sizeがある場合は追加
              if (item.size !== undefined && item.size !== null) {
                priceEntry.size = item.size;
              }

              // print_typeがある場合は追加（オンデマンド/オフセット表示用）
              if (item.print_type !== undefined && item.print_type !== null) {
                priceEntry.print_type = item.print_type;
              }

              variantPriceData.push(priceEntry);
            }
          });
        }

        if (index === 0) {
          console.log('[Price Data Processor] Single-variant multiple-paper format detected:', {
            paperKeys: availableVariationKeys,
            processedVariations: Object.keys(variationsData).length
          });
        }
      } else if (isBookletProduct) {
        // 冊子商品: すべてのvariationsを処理
        hasVariantMetafields = true;

        // 正規化形式の場合: variation_keyは「表紙紙質_本文紙質」形式
        if (isNormalizedBookletFormat) {
          for (const [variationKey, variationArray] of Object.entries(matrixData.variations)) {
            if (!variationArray || !Array.isArray(variationArray)) continue;

            // variation_keyから表紙紙質と本文紙質を抽出
            const parts = variationKey.split(window.VARIATION_CODE_SEPARATOR);
            let coverPaper, bodyPaperFromKey;
            if (parts.length === 2) {
              coverPaper = parts[0];       // 表紙紙質
              bodyPaperFromKey = parts[1]; // 本文紙質
            } else {
              // パターンが合わない場合はスキップ
              continue;
            }

            // 数量を抽出
            const quantities = [...new Set(variationArray.map(item => item.quantity))].sort((a, b) => a - b);

            // variationsDataに追加（表紙紙質をキーとして使用、本文紙質はbodyPaperフィールドで管理）
            // 同じ表紙紙質で複数の本文紙質がある場合はマージ
            if (!variationsData[coverPaper]) {
              variationsData[coverPaper] = {
                paper: null,
                printColor: variantOptions['印刷色数'] || null,
                pageCount: variantOptions['ページ数'] || variantOptions['ページ範囲'] || null,
                bodyPapers: [bodyPaperFromKey],
                coverPaper: coverPaper,
                quantities: quantities
              };
            } else {
              // 本文紙質を追加（重複を避ける）
              if (!variationsData[coverPaper].bodyPapers.includes(bodyPaperFromKey)) {
                variationsData[coverPaper].bodyPapers.push(bodyPaperFromKey);
              }
              // 数量もマージ
              quantities.forEach(q => {
                if (!variationsData[coverPaper].quantities.includes(q)) {
                  variationsData[coverPaper].quantities.push(q);
                }
              });
              variationsData[coverPaper].quantities.sort((a, b) => a - b);
            }

            // 配列をpriceDataに追加
            // APIレスポンスは delivery_days/price、Liquidは delivery_time/base_price を使用するため両方に対応
            variationArray.forEach(item => {
              const price = item.base_price || item.price;
              let deliveryTime = item.delivery_time;
              if (!deliveryTime && item.delivery_days !== undefined) {
                deliveryTime = `${item.delivery_days + 1}日`;
              }

              if (item.quantity && deliveryTime && price && price > 0) {
                const priceEntry = {
                  variation_code: coverPaper,  // 表紙紙質のみ（統一設計: body_paperがある場合）
                  quantity: item.quantity,
                  delivery_time: deliveryTime,
                  base_price: price,
                  cover_paper: coverPaper,  // 表紙紙質
                  body_paper: bodyPaperFromKey  // 本文紙質（別フィールドでフィルタリング）
                };

                // page_countがある場合は追加
                if (item.page_count !== undefined && item.page_count !== null) {
                  priceEntry.page_count = item.page_count;
                }

                // sizeがある場合は追加
                if (item.size !== undefined && item.size !== null) {
                  priceEntry.size = item.size;
                }

                // print_typeがある場合は追加（オンデマンド/オフセット表示用）
                if (item.print_type !== undefined && item.print_type !== null) {
                  priceEntry.print_type = item.print_type;
                }

                variantPriceData.push(priceEntry);
              }
            });
          }

          if (index === 0) {
            console.log('[Price Data Processor] Normalized booklet format detected:', {
              originalVariationKeys: availableVariationKeys.length,
              coverPapers: Object.keys(variationsData).length,
              sampleCoverPaper: Object.keys(variationsData)[0],
              priceDataCount: variantPriceData.length
            });
          }
        } else {
          // 従来形式: bodyPaperはバリアントオプションから取得
          // 統一紙セレクターモードかどうかをチェック
          // 1. price_matrix自体のoptions_configをチェック（最も信頼性が高い）
          // 2. window.productData や window._paper_selector_mode をフォールバック
          const isUnifiedPaperModeFromMatrix = matrixData.options_config?.paper_selector === 'unified';
          const isUnifiedPaperMode = isUnifiedPaperModeFromMatrix ||
            window.productData?.paper_selector_mode === 'unified' ||
            window._paper_selector_mode === 'unified';

          for (const [coverPaperKey, variationArray] of Object.entries(matrixData.variations)) {
            if (!variationArray || !Array.isArray(variationArray)) continue;

            // 数量を抽出
            const quantities = [...new Set(variationArray.map(item => item.quantity))].sort((a, b) => a - b);

            // variationsDataに追加
            // 統一モードの場合は単一紙質キー、従来モードの場合は掛け合わせキーを使用
            const variationKey = isUnifiedPaperMode ? coverPaperKey : `${bodyPaper}_${coverPaperKey}`;
            variationsData[variationKey] = {
              paper: variantOptions['用紙'] || null,
              printColor: variantOptions['印刷色数'] || null,
              pageCount: variantOptions['ページ数'] || variantOptions['ページ範囲'] || null,
              bodyPaper: isUnifiedPaperMode ? null : bodyPaper,  // 統一モードではnull
              coverPaper: coverPaperKey,
              quantities: quantities
            };

            // 配列をpriceDataに追加
            // APIレスポンスは delivery_days/price、Liquidは delivery_time/base_price を使用するため両方に対応
            variationArray.forEach(item => {
              const price = item.base_price || item.price;
              let deliveryTime = item.delivery_time;
              if (!deliveryTime && item.delivery_days !== undefined) {
                deliveryTime = `${item.delivery_days + 1}日`;
              }

              if (item.quantity && deliveryTime && price && price > 0) {
                const priceEntry = {
                  variation_code: coverPaperKey,  // 表紙紙質をvariation_codeとして使用
                  quantity: item.quantity,
                  delivery_time: deliveryTime,
                  base_price: price
                };

                // 統一モードでない場合のみbody_paperを追加
                if (!isUnifiedPaperMode && bodyPaper) {
                  priceEntry.body_paper = bodyPaper;
                }

                // page_countがある場合は追加
                if (item.page_count !== undefined && item.page_count !== null) {
                  priceEntry.page_count = item.page_count;
                }

                // sizeがある場合は追加
                if (item.size !== undefined && item.size !== null) {
                  priceEntry.size = item.size;
                }

                // print_typeがある場合は追加（オンデマンド/オフセット表示用）
                if (item.print_type !== undefined && item.print_type !== null) {
                  priceEntry.print_type = item.print_type;
                }

                variantPriceData.push(priceEntry);
              }
            });
          }

          if (index === 0) {
            console.log('[Price Data Processor] Traditional booklet format detected:', {
              bodyPaper,
              coverPaperKeys: availableVariationKeys,
              processedVariations: Object.keys(variationsData).length,
              isUnifiedPaperMode: isUnifiedPaperMode,
              isUnifiedPaperModeFromMatrix: isUnifiedPaperModeFromMatrix
            });
          }
        }
      } else {
        // 通常商品: price_matrixの全variationsを処理
        // チラシ商品などでは、各バリアントのprice_matrixに「印刷色数×全用紙」の組み合わせが含まれる
        const availableVariationKeys = Object.keys(matrixData.variations || {});

        if (availableVariationKeys.length > 0) {
          hasVariantMetafields = true;
          processedCount++;

          // 全てのvariationを処理
          for (const variationKey of availableVariationKeys) {
            const variationArray = matrixData.variations[variationKey];

            if (!variationArray || !Array.isArray(variationArray) || variationArray.length === 0) {
              continue;
            }

            // 有効な価格データがあるかチェック
            // APIレスポンスは delivery_days/price、Liquidは delivery_time/base_price を使用
            const hasValidPriceData = variationArray.some(item => {
              const hasQuantity = !!item.quantity;
              const hasDelivery = item.delivery_time !== undefined || item.delivery_days !== undefined;
              const price = item.base_price || item.price;
              const hasPrice = price && price > 0;
              return hasQuantity && hasDelivery && hasPrice;
            });

            // 有効な価格データがない場合はスキップ
            if (!hasValidPriceData) {
              continue;
            }

            // variation_codeから用紙と印刷色数を抽出
            const parts = variationKey.split(window.VARIATION_CODE_SEPARATOR);
            let extractedPaper = null;
            let extractedPrintColor = null;
            if (parts.length >= 2) {
              extractedPaper = parts[0];
              extractedPrintColor = parts.slice(1).join(window.VARIATION_CODE_SEPARATOR);
            }

            // 数量を抽出
            const quantities = [...new Set(variationArray.map(item => item.quantity))].sort((a, b) => a - b);

            // variationsDataに追加（メタデータのみ。初回のみ設定、以降は数量をマージ）
            // ★修正: 価格データ(variantPriceData)は後続処理で必ず追加する
            if (!variationsData[variationKey]) {
              variationsData[variationKey] = {
                paper: extractedPaper || variantOptions['用紙'] || null,
                printColor: extractedPrintColor || variantOptions['印刷色数'] || null,
                pageCount: variantOptions['ページ数'] || variantOptions['ページ範囲'] || null,
                quantities: quantities
              };
            } else {
              // 既存のvariationsDataがある場合は数量をマージ
              const existingQuantities = variationsData[variationKey].quantities || [];
              const mergedQuantities = [...new Set([...existingQuantities, ...quantities])].sort((a, b) => a - b);
              variationsData[variationKey].quantities = mergedQuantities;
            }

            // 配列をpriceDataに追加
            // APIレスポンスは delivery_days/price、Liquidは delivery_time/base_price を使用するため両方に対応
            variationArray.forEach(item => {
              const price = item.base_price || item.price;
              // delivery_timeがある場合はそのまま使用、なければdelivery_daysから変換
              let deliveryTime = item.delivery_time;
              if (!deliveryTime && item.delivery_days !== undefined) {
                // delivery_days: 0 → "1日", 1 → "2日", etc.
                deliveryTime = `${item.delivery_days + 1}日`;
              }

              if (item.quantity && deliveryTime && price && price > 0) {
                const priceEntry = {
                  variation_code: variationKey,
                  quantity: item.quantity,
                  delivery_time: deliveryTime,
                  base_price: price
                };

                // page_countがある場合は追加
                if (item.page_count !== undefined && item.page_count !== null) {
                  priceEntry.page_count = item.page_count;
                }

                // sizeがある場合は追加
                if (item.size !== undefined && item.size !== null) {
                  priceEntry.size = item.size;
                }

                // 本文紙質がある場合は追加
                if (bodyPaper) {
                  priceEntry.body_paper = bodyPaper;
                }

                // print_typeがある場合は追加（オンデマンド/オフセット表示用）
                if (item.print_type !== undefined && item.print_type !== null) {
                  priceEntry.print_type = item.print_type;
                }

                variantPriceData.push(priceEntry);
              }
            });
          }
        } else {
          skippedNoMatchCount++;
        }
      }
    });

    // 処理時間のログ
    const endTime = performance.now();
    const processingTime = (endTime - startTime).toFixed(2);

    console.log(`[Price Data Processor] Processing completed in ${processingTime}ms`, {
      totalVariants: variantMetafields.length,
      processedCount,
      skippedNoMatrixCount,
      skippedNoMatchCount,
      skippedByFilterCount,
      filterPageRange,
      priceDataEntries: variantPriceData.length,
      variationsCount: Object.keys(variationsData).length
    });

    return {
      variantPriceData,
      variationsData,
      hasVariantMetafields
    };
  }

  /**
   * 価格データから印刷方式の種類を取得
   * @param {Array} priceData - 価格データ配列
   * @param {string} variationCode - (オプション) 特定のvariation_codeでフィルタ
   * @returns {Array} - 印刷方式の配列（例: ['オンデマンド', 'オフセット']）
   */
  function getPrintTypesInPriceData(priceData, variationCode = null) {
    if (!Array.isArray(priceData)) return [];

    let filtered = priceData;
    if (variationCode) {
      filtered = priceData.filter(p => p.variation_code === variationCode);
    }

    const types = new Set();
    filtered.forEach(entry => {
      if (entry.print_type) {
        types.add(entry.print_type);
      }
    });

    return Array.from(types);
  }

  /**
   * 価格データに複数の印刷方式が混在しているかチェック
   * @param {Array} priceData - 価格データ配列
   * @param {string} variationCode - (オプション) 特定のvariation_codeでフィルタ
   * @returns {boolean} - 混在している場合true
   */
  function hasMixedPrintTypes(priceData, variationCode = null) {
    const types = getPrintTypesInPriceData(priceData, variationCode);
    return types.length > 1;
  }

  /**
   * 特定の数量の印刷方式を取得
   * @param {Array} priceData - 価格データ配列
   * @param {number} quantity - 数量
   * @param {string} variationCode - (オプション) variation_code
   * @returns {string|null} - 印刷方式（見つからない場合null）
   */
  function getPrintTypeForQuantity(priceData, quantity, variationCode = null) {
    if (!Array.isArray(priceData)) return null;

    let filtered = priceData;
    if (variationCode) {
      filtered = priceData.filter(p => p.variation_code === variationCode);
    }

    // 1. price_dataにprint_typeがある場合はそれを使用
    const entry = filtered.find(p => p.quantity === quantity && p.print_type);
    if (entry) {
      return entry.print_type;
    }

    // 2. print_type_configメタフィールドから自動判定
    const config = window.productData?.print_type_config;
    if (config) {
      return determinePrintTypeByQuantity(quantity, config);
    }

    return null;
  }

  /**
   * print_type_configに基づいて数量から印刷方式を判定
   * @param {number} quantity - 数量
   * @param {Object} config - print_type_config設定
   *   - ondemand_max: オンデマンドの最大部数（この数量以下はオンデマンド）
   *   - offset_min: オフセットの最小部数（この数量以上はオフセット）
   * @returns {string|null} - 印刷方式
   */
  function determinePrintTypeByQuantity(quantity, config) {
    if (!config || typeof quantity !== 'number') return null;

    const ondemandMax = config.ondemand_max;
    const offsetMin = config.offset_min;

    // ondemand_maxが設定されており、数量がそれ以下の場合
    if (ondemandMax !== undefined && quantity <= ondemandMax) {
      return 'オンデマンド';
    }

    // offset_minが設定されており、数量がそれ以上の場合
    if (offsetMin !== undefined && quantity >= offsetMin) {
      return 'オフセット';
    }

    // どちらにも該当しない場合（ギャップがある場合など）
    // ondemand_maxより大きくoffset_minより小さい場合はnull
    return null;
  }

  /**
   * 価格データにprint_typeを自動付与（print_type_configに基づく）
   * @param {Array} priceData - 価格データ配列
   * @param {Object} config - print_type_config設定（オプション、省略時はwindow.productDataから取得）
   * @returns {Array} - print_typeが付与された価格データ配列
   */
  function applyPrintTypeConfig(priceData, config = null) {
    if (!Array.isArray(priceData)) return priceData;

    const printTypeConfig = config || window.productData?.print_type_config;
    if (!printTypeConfig) return priceData;

    return priceData.map(entry => {
      // 既にprint_typeがある場合はスキップ
      if (entry.print_type) return entry;

      const printType = determinePrintTypeByQuantity(entry.quantity, printTypeConfig);
      if (printType) {
        return { ...entry, print_type: printType };
      }
      return entry;
    });
  }

  // グローバルに公開
  window.matchVariationCodeInMatrix = matchVariationCodeInMatrix;
  window.processPriceData = processPriceData;
  window.getPrintTypesInPriceData = getPrintTypesInPriceData;
  window.hasMixedPrintTypes = hasMixedPrintTypes;
  window.getPrintTypeForQuantity = getPrintTypeForQuantity;
  window.determinePrintTypeByQuantity = determinePrintTypeByQuantity;
  window.applyPrintTypeConfig = applyPrintTypeConfig;

})();

